import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiSend, FiUsers, FiClock, FiCheck, FiHistory, FiEye, FiImage, FiUser } = FiIcons;

const EmailCampaign = () => {
  const location = useLocation();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [audience, setAudience] = useState('all');
  const [singleRecipient, setSingleRecipient] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sentSuccess, setSentSuccess] = useState(false);
  const [history, setHistory] = useState([]);
  const [subscriberCount, setSubscriberCount] = useState(0);

  // Load History & Subscriber Count & Check for Passed State
  useEffect(() => {
    const storedHistory = localStorage.getItem('ramirez_email_history');
    if (storedHistory) setHistory(JSON.parse(storedHistory));
    
    const storedSubs = JSON.parse(localStorage.getItem('ramirez_subscribers') || '[]');
    setSubscriberCount(storedSubs.length);

    // Handle data passed from Subscribers page
    if (location.state) {
      if (location.state.audience === 'single') {
        setAudience('single');
        setSingleRecipient(location.state.email || '');
      }
      if (location.state.message) setMessage(location.state.message);
      if (location.state.subject) setSubject(location.state.subject);
    }
  }, [location]);

  const handleSend = (e) => {
    e.preventDefault();
    setIsSending(true);

    // Simulate API call
    setTimeout(() => {
      setIsSending(false);
      setSentSuccess(true);
      
      const newEntry = {
        id: Date.now(),
        date: new Date().toLocaleDateString(),
        time: new Date().toLocaleTimeString(),
        subject: subject,
        audience: audience === 'single' ? `Single (${singleRecipient})` : audience,
        status: 'Sent',
        recipientCount: audience === 'all' ? subscriberCount : audience === 'single' ? 1 : Math.floor(subscriberCount / 2)
      };

      const updatedHistory = [newEntry, ...history];
      setHistory(updatedHistory);
      localStorage.setItem('ramirez_email_history', JSON.stringify(updatedHistory));
      
      setSubject('');
      setMessage('');
      if (audience === 'single') setSingleRecipient('');
      
      setTimeout(() => setSentSuccess(false), 3000);
    }, 2000);
  };

  const getRecipientLabel = () => {
    if (audience === 'all') return `All Active Subscribers (~${subscriberCount})`;
    if (audience === 'due') return 'Inspection Due Soon (~3)';
    if (audience === 'leads') return 'Recent Leads (~5)';
    if (audience === 'single') return singleRecipient ? `Single: ${singleRecipient}` : 'One Recipient';
    return 'Selected Audience';
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">Email Campaigns</h1>
        <p className="text-gray-400">Design and send mass email updates or individual client notifications.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Compose Section */}
        <div className="space-y-8">
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-xl">
            <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
              <SafeIcon icon={FiMail} className="text-blue-500" />
              Compose Email
            </h2>
            
            <form onSubmit={handleSend} className="space-y-6">
              {/* Audience Selector */}
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Target Audience
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <label className={`cursor-pointer border rounded-lg p-3 flex flex-col items-center gap-2 transition-all ${audience === 'all' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-700 border-gray-600 text-gray-400 hover:bg-gray-600'}`}>
                    <input type="radio" name="audience" value="all" className="hidden" checked={audience === 'all'} onChange={(e) => setAudience(e.target.value)} />
                    <span className="font-bold text-xs">All</span>
                  </label>
                  <label className={`cursor-pointer border rounded-lg p-3 flex flex-col items-center gap-2 transition-all ${audience === 'due' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-700 border-gray-600 text-gray-400 hover:bg-gray-600'}`}>
                    <input type="radio" name="audience" value="due" className="hidden" checked={audience === 'due'} onChange={(e) => setAudience(e.target.value)} />
                    <span className="font-bold text-xs">Due Soon</span>
                  </label>
                  <label className={`cursor-pointer border rounded-lg p-3 flex flex-col items-center gap-2 transition-all ${audience === 'leads' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-700 border-gray-600 text-gray-400 hover:bg-gray-600'}`}>
                    <input type="radio" name="audience" value="leads" className="hidden" checked={audience === 'leads'} onChange={(e) => setAudience(e.target.value)} />
                    <span className="font-bold text-xs">Leads</span>
                  </label>
                  <label className={`cursor-pointer border rounded-lg p-3 flex flex-col items-center gap-2 transition-all ${audience === 'single' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-700 border-gray-600 text-gray-400 hover:bg-gray-600'}`}>
                    <input type="radio" name="audience" value="single" className="hidden" checked={audience === 'single'} onChange={(e) => setAudience(e.target.value)} />
                    <span className="font-bold text-xs">Single</span>
                  </label>
                </div>
                <p className="text-xs text-blue-400 mt-2 text-right">Targeting: {getRecipientLabel()}</p>
              </div>

               {/* Conditional Input for Single Recipient */}
               {audience === 'single' && (
                <div className="animate-fade-in-up">
                  <label className="block text-gray-300 text-sm font-medium mb-2">Recipient Email Address</label>
                  <div className="relative">
                    <SafeIcon icon={FiMail} className="absolute left-3 top-3.5 text-gray-500" />
                    <input 
                      type="email" 
                      value={singleRecipient} 
                      onChange={(e) => setSingleRecipient(e.target.value)}
                      placeholder="client@example.com"
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg pl-10 pr-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none"
                      required={audience === 'single'}
                    />
                  </div>
                </div>
              )}

              {/* Subject Line */}
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Subject Line</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g. Important Regulatory Update: CARB 2024"
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none"
                  required
                />
              </div>

              {/* Body */}
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Email Body</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Draft your message here..."
                  rows="8"
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none resize-y font-sans"
                  required
                ></textarea>
                <div className="flex justify-between mt-2">
                  <button type="button" className="text-xs text-gray-400 hover:text-white flex items-center gap-1">
                    <SafeIcon icon={FiImage} /> Insert Image
                  </button>
                  <span className="text-xs text-gray-500">HTML supported</span>
                </div>
              </div>

              {/* Send Button */}
              <div className="pt-4 border-t border-gray-700">
                <button
                  type="submit"
                  disabled={isSending || !message.trim() || !subject.trim() || (audience === 'single' && !singleRecipient)}
                  className={`w-full font-bold py-4 rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg ${isSending ? 'bg-gray-600 cursor-not-allowed text-gray-300' : sentSuccess ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-blue-900/50'}`}
                >
                  {isSending ? (
                    <>Processing...</>
                  ) : sentSuccess ? (
                    <>
                      <SafeIcon icon={FiCheck} className="text-xl" />
                      <span>Campaign Sent!</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiSend} className="text-xl" />
                      <span>Send Campaign</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Right Column: Preview & History */}
        <div className="space-y-8">
          {/* Email Preview Card */}
          <div className="bg-white rounded-xl overflow-hidden shadow-xl border border-gray-700 sticky top-8">
            <div className="bg-gray-100 p-3 border-b border-gray-200 flex justify-between items-center">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                <SafeIcon icon={FiEye} /> Live Preview
              </span>
              <div className="flex gap-1">
                <div className="w-2 h-2 rounded-full bg-red-400"></div>
                <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
                <div className="w-2 h-2 rounded-full bg-green-400"></div>
              </div>
            </div>
            
            <div className="p-6">
              {/* Mock Gmail Header */}
              <div className="mb-6 pb-4 border-b border-gray-100 space-y-1">
                <h3 className="text-lg font-bold text-gray-900 leading-tight">
                  {subject || "Your Subject Line Will Appear Here"}
                </h3>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xs">R</div>
                  <div>
                    <p className="text-sm font-bold text-gray-800">Ramirez Mobile <span className="text-xs font-normal text-gray-500">&lt;admin@ramirezmobile.com&gt;</span></p>
                    <p className="text-xs text-gray-500">to {audience === 'single' ? (singleRecipient || 'Recipient') : audience === 'all' ? 'Everyone' : 'Selected List'}</p>
                  </div>
                </div>
              </div>
              
              {/* Email Body */}
              <div className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap min-h-[150px]">
                {message || (
                  <span className="text-gray-300 italic">
                    Start typing to see how your email will look...
                  </span>
                )}
              </div>
              
              {/* Mock Footer */}
              <div className="mt-8 pt-4 border-t border-gray-100 text-center">
                <p className="text-xs text-gray-400">
                  Ramirez Mobile Clean Truck Services<br/>
                  Ontario, CA 91761 • (909) 284-2738
                </p>
                <p className="text-[10px] text-gray-300 mt-2">
                  You are receiving this email because you subscribed to fleet alerts. <a href="#" className="text-blue-400 underline">Unsubscribe</a>.
                </p>
              </div>
            </div>
          </div>

          {/* History */}
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-xl">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <SafeIcon icon={FiHistory} className="text-gray-400" />
              Campaign History
            </h2>
            <div className="space-y-4 max-h-60 overflow-y-auto custom-scrollbar pr-1">
              {history.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">No emails sent yet.</p>
              ) : (
                history.map(item => (
                  <div key={item.id} className="border-b border-gray-700 pb-3 last:border-0 last:pb-0 hover:bg-gray-700/30 p-2 rounded transition-colors">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-gray-400">{item.date}</span>
                      <span className="text-xs bg-green-900/30 text-green-400 px-2 py-0.5 rounded border border-green-800 flex items-center gap-1">
                        <SafeIcon icon={FiCheck} className="text-[10px]" /> {item.status} ({item.recipientCount})
                      </span>
                    </div>
                    <p className="text-sm font-bold text-white truncate">{item.subject}</p>
                    <p className="text-xs text-blue-400 mt-1">Audience: {item.audience}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default EmailCampaign;